/**
 * The ad accounts: Google Ads (the practice connects it) and Meta Ads (managed by the agency, with the Pixel ID).
 * Shown in Settings > Paid > Ad campaigns.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Facebook, Megaphone } from 'lucide-react';
import { integrationsApi, type GoogleAdsStatus, type MetaAdsStatus } from '../api/integrationsApi';
import { integrationKeys } from '../queryKeys';
import { IntegrationCard } from './IntegrationCard';
import { Button } from '@/shared/ui/Button';
import { SkeletonCard } from '@/shared/ui/Skeleton';
import { useToast } from '@/shared/ui/Toast';
import { buildMetadata, mapStatus } from '../utils/integrationStatus';
import styles from '../pages/SettingsPage.module.css';

export function GoogleAdsCard({ practiceId, canSync }: { practiceId: string; canSync: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: integrationKeys.googleAdsStatus(practiceId),
    queryFn: () => integrationsApi.googleAds.getStatus().then((r) => r.data),
    enabled: !!practiceId,
  });

  const connectMutation = useMutation({
    mutationFn: () => integrationsApi.googleAds.initiateConnect().then((r) => r.data),
    onSuccess: ({ authUrl }) => {
      window.location.href = authUrl;
    },
    onError: () => toast.error('Failed to start Google Ads connection'),
  });

  const disconnectMutation = useMutation({
    mutationFn: () => integrationsApi.googleAds.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.googleAdsStatus(practiceId) });
      toast.success('Google Ads disconnected');
    },
    onError: () => toast.error('Failed to disconnect'),
  });

  const syncMutation = useMutation({
    mutationFn: () => integrationsApi.googleAds.triggerSync(),
    onSuccess: () => toast.success('Sync queued — data will update shortly'),
    onError: () => toast.error('Failed to trigger sync'),
  });

  if (isLoading) return <SkeletonCard />;

  const status = mapStatus(data);
  const metadata = buildMetadata(data, (d: GoogleAdsStatus) =>
    d.customerId ? [{ label: 'Customer ID', value: d.customerId }] : [],
  );

  return (
    <IntegrationCard
      displayName="Google Ads"
      description="Sync campaign performance data to view spend, conversions, and ROAS."
      icon={Megaphone}
      status={status}
      metadata={metadata}
      errorMessage={data?.lastSyncError ?? undefined}
      onConnect={() => connectMutation.mutate()}
      onDisconnect={canSync ? () => disconnectMutation.mutate() : undefined}
      onSync={canSync ? () => syncMutation.mutate() : undefined}
      connectLoading={connectMutation.isPending}
      disconnectLoading={disconnectMutation.isPending}
      syncLoading={syncMutation.isPending}
    />
  );
}

export function MetaAdsCard({ practiceId, canSync }: { practiceId: string; canSync: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const { data, isLoading } = useQuery({
    queryKey: integrationKeys.metaAdsStatus(practiceId),
    queryFn: () => integrationsApi.metaAds.getStatus().then((r) => r.data),
    enabled: !!practiceId,
  });

  const [pixelId, setPixelId] = useState('');
  const [pixelDirty, setPixelDirty] = useState(false);

  useEffect(() => {
    if (data?.pixelId) setPixelId(data.pixelId);
  }, [data]);

  const disconnectMutation = useMutation({
    mutationFn: () => integrationsApi.metaAds.disconnect(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.metaAdsStatus(practiceId) });
      toast.success('Meta Ads disconnected');
    },
    onError: () => toast.error('Failed to disconnect'),
  });

  const syncMutation = useMutation({
    mutationFn: () => integrationsApi.metaAds.triggerSync(),
    onSuccess: () => toast.success('Sync queued — data will update shortly'),
    onError: () => toast.error('Failed to trigger sync'),
  });

  const pixelMutation = useMutation({
    mutationFn: () => integrationsApi.metaAds.updateConfig(pixelId || null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: integrationKeys.metaAdsStatus(practiceId) });
      toast.success('Pixel ID saved');
      setPixelDirty(false);
    },
    onError: () => toast.error('Failed to save Pixel ID'),
  });

  if (isLoading) return <SkeletonCard />;

  const status = mapStatus(data);
  const isConnected = status === 'CONNECTED';
  const metadata = buildMetadata(data, (d: MetaAdsStatus) =>
    d.adAccountName ? [{ label: 'Ad Account', value: d.adAccountName }] : [],
  );

  return (
    <div className={styles.metaCard}>
      <IntegrationCard
        displayName="Meta Ads"
        description="Sync Facebook and Instagram campaign data to view spend, reach, and leads."
        icon={Facebook}
        status={status}
        metadata={metadata}
        errorMessage={data?.lastSyncError ?? undefined}
        managedNote="Managed by your agency"
        onDisconnect={canSync ? () => disconnectMutation.mutate() : undefined}
        onSync={canSync ? () => syncMutation.mutate() : undefined}
        disconnectLoading={disconnectMutation.isPending}
        syncLoading={syncMutation.isPending}
      />
      {isConnected && canSync && (
        <div className={styles.metaConfig}>
          <p className={styles.metaConfigLabel}>Meta Pixel ID</p>
          <p className={styles.metaObjectiveDesc}>
            Required for campaigns using the &ldquo;Leads — Website (Pixel)&rdquo; objective.
          </p>
          <div className={styles.metaConfigFooter}>
            <input
              className={styles.metaPixelInput}
              type="text"
              placeholder="e.g. 1234567890"
              value={pixelId}
              onChange={(e) => {
                setPixelId(e.target.value);
                setPixelDirty(true);
              }}
            />
            {pixelDirty && (
              <Button
                size="sm"
                onClick={() => pixelMutation.mutate()}
                isLoading={pixelMutation.isPending}
              >
                Save
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
